import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PlatformAuthProvider } from './context/PlatformAuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import DashboardLayout from './pages/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Leave from './pages/Leave';
import LeaveRequests from './pages/LeaveRequests';
import Billing from './pages/Billing';
import Tutorials from './pages/Tutorials';
import Contact from './pages/Contact';
import DashboardAnalytics from './pages/DashboardAnalytics';
import About from './pages/About';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import Privacy from './pages/legal/Privacy';
import Terms from './pages/legal/Terms';
import Security from './pages/legal/Security';
import RTI from './pages/legal/RTI';
import UserManagement from './pages/UserManagement';
import LeaveConfig from './pages/LeaveConfig';
import EmployeeDirectory from './pages/EmployeeDirectory';
import EmployeeProfile from './pages/EmployeeProfile';
import WorkflowConfig from './pages/WorkflowConfig';
import LeaveDashboard from './pages/LeaveDashboard';
import MyProfile from './pages/MyProfile';
import OrgChart from './pages/OrgChart';
import TeamCalendar from './pages/TeamCalendar';
import SchoolBranding from './pages/SchoolBranding';
import SchoolAnnouncements from './pages/SchoolAnnouncements';
import DocumentsPage from './pages/DocumentsPage';
import AuthCallback from './components/auth/AuthCallback';
import HolidayCalendar from './pages/HolidayCalendar';
import WorkSchedule from './pages/WorkSchedule';
import ClassManagement from './pages/dashboard/ClassManagement';
import StudentManagement from './pages/dashboard/StudentManagement';
import ParentLogin from './pages/ParentLogin';
import ParentDashboard from './pages/ParentDashboard';
import ParentAttendance from './pages/ParentAttendance';
import ParentReportCard from './pages/ParentReportCard';
import ParentFees from './pages/ParentFees';
import StudentLogin from './pages/StudentLogin';
import StudentDashboard from './pages/StudentDashboard';
import FeeManagement from './pages/dashboard/FeeManagement';
import TicketManagement from './pages/dashboard/TicketManagement';
import ExamManagement from './pages/dashboard/ExamManagement';
import ModuleConfig from './pages/dashboard/ModuleConfig';
import AttendanceManagement from './pages/dashboard/AttendanceManagement';

// Platform Admin
import PlatformLogin from './pages/platform/PlatformLogin';
import PlatformLayout from './pages/platform/PlatformLayout';
import PlatformDashboard from './pages/platform/PlatformDashboard';
import PlatformTenants from './pages/platform/PlatformTenants';
import PlatformRevenue from './pages/platform/PlatformRevenue';
import PlatformMessages from './pages/platform/PlatformMessages';
import PlatformAnnouncements from './pages/platform/PlatformAnnouncements';
import PlatformModuleConfig from './pages/platform/PlatformModuleConfig';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children, allowHR = true }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  const allowed = user?.role === 'school_admin' || (allowHR && user?.role === 'hr');
  return allowed ? children : <Navigate to="/dashboard" replace />;
};

const SchoolAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user?.role === 'school_admin' ? children : <Navigate to="/dashboard" replace />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  // CRITICAL: If returning from Google OAuth, render AuthCallback instead
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }
  
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

function AppContent() {
  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/tutorials" element={<Tutorials />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<About />} />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/security" element={<Security />} />
        <Route path="/rti" element={<RTI />} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/parent/login" element={<Navigate to="/login" replace />} />
        <Route path="/parent/dashboard" element={<ParentDashboard />} />
        <Route path="/parent/attendance" element={<ParentAttendance />} />
        <Route path="/parent/report-card" element={<ParentReportCard />} />
        <Route path="/parent/fees" element={<ParentFees />} />
        <Route path="/student/login" element={<Navigate to="/login" replace />} />
        <Route path="/student/dashboard" element={<StudentDashboard />} />
        
        <Route path="/dashboard" element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="leave" element={<LeaveDashboard />} />
          <Route path="leave-old" element={<Leave />} />
          <Route path="profile" element={<MyProfile />} />
          <Route path="requests" element={<AdminRoute><LeaveRequests /></AdminRoute>} />
          <Route path="users" element={<AdminRoute><UserManagement /></AdminRoute>} />
          <Route path="employees" element={<AdminRoute><EmployeeDirectory /></AdminRoute>} />
          <Route path="employee/:userId" element={<EmployeeProfile />} />
          <Route path="org-chart" element={<OrgChart />} />
          <Route path="team-calendar" element={<AdminRoute><TeamCalendar /></AdminRoute>} />
          <Route path="branding" element={<SchoolAdminRoute><SchoolBranding /></SchoolAdminRoute>} />
          <Route path="announcements" element={<SchoolAdminRoute><SchoolAnnouncements /></SchoolAdminRoute>} />
          <Route path="holidays" element={<AdminRoute><HolidayCalendar /></AdminRoute>} />
          <Route path="work-schedule" element={<AdminRoute><WorkSchedule /></AdminRoute>} />
          <Route path="leave-config" element={<SchoolAdminRoute><LeaveConfig /></SchoolAdminRoute>} />
          <Route path="workflows" element={<SchoolAdminRoute><WorkflowConfig /></SchoolAdminRoute>} />
          <Route path="classes" element={<AdminRoute><ClassManagement /></AdminRoute>} />
          <Route path="students" element={<AdminRoute><StudentManagement /></AdminRoute>} />
          <Route path="fees" element={<AdminRoute><FeeManagement /></AdminRoute>} />
          <Route path="tickets" element={<AdminRoute><TicketManagement /></AdminRoute>} />
          <Route path="exams" element={<AdminRoute><ExamManagement /></AdminRoute>} />
          <Route path="modules" element={<SchoolAdminRoute><ModuleConfig /></SchoolAdminRoute>} />
          <Route path="attendance" element={<AdminRoute><AttendanceManagement /></AdminRoute>} />
          <Route path="billing" element={<AdminRoute><Billing /></AdminRoute>} />
          <Route path="analytics" element={<AdminRoute><DashboardAnalytics /></AdminRoute>} />
          <Route path="documents" element={<AdminRoute><DocumentsPage /></AdminRoute>} />
        </Route>

        {/* Platform Admin Routes */}
        <Route path="/platform/login" element={<PlatformLogin />} />
        <Route path="/platform" element={<PlatformLayout />}>
          <Route path="dashboard" element={<PlatformDashboard />} />
          <Route path="tenants" element={<PlatformTenants />} />
          <Route path="modules" element={<PlatformModuleConfig />} />
          <Route path="revenue" element={<PlatformRevenue />} />
          <Route path="messages" element={<PlatformMessages />} />
          <Route path="announcements" element={<PlatformAnnouncements />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <PlatformAuthProvider>
            <AppContent />
          </PlatformAuthProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
